/**
 * Campaign Contacts page with individual contact stats modal.
 * Route: /marketing/campaigns/:id/contacts
 *
 * Replicates PHP campaign-contacts.php including:
 *  - Stats mini-badges per row (opens, clicks, spam, unsub)
 *  - Row click opens contact stats modal (for non-pending contacts)
 *  - Full contact stats modal with delivery timeline, engagement, clicked URLs
 */
import { useState, useCallback, useMemo } from 'react';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Loader2, Plus, Trash2, Search, X, RotateCcw } from 'lucide-react';
import { DataTable } from '@/components/ui/DataTable';
import type { DataTableColumn } from '@/components/ui/DataTable';
import { Input } from '@/components/ui/Input';
import { FilterSelect } from '@/components/ui/FilterSelect';
import type { FilterSelectOption } from '@/components/ui/FilterSelect';
import { useToast } from '@/hooks/useToast';
import { useUserTimezone } from '@/hooks/useUserTimezone';
import { formatPacificDateTime } from '@/lib/facilityTimezone';
import * as campaignsSvc from '@/services/campaigns.service';
import type { CampaignContact, ContactStatsResponse, CampaignContactReply } from '@/services/campaigns.service';
import { apiClient } from '@/services/apiClient';
import { ContactLeadCell, ContactLeadFilter, ContactLeadList, ContactLeadOpener, leadFilterFromUrl, leadParam, useContactLeadModal } from '@/components/marketing/ContactLeads';

// ─── Types ───────────────────────────────────────────────────────────────────

interface PatientResult {
  patientID: number;
  firstName: string;
  lastName: string;
  email: string;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

// Contact, event and reply timestamps arrive as Pacific wall-clock DATETIME
// strings (config('app.timezone')) with no offset; reproject into the
// viewer's zone and suffix the abbreviation so each time is unambiguous.
function fmtDate(dateStr: string | null | undefined, tz: string): string {
  return formatPacificDateTime(dateStr, tz);
}

// ─── Component ───────────────────────────────────────────────────────────────

export default function EmailCampaignContactsPage() {
  const { id } = useParams<{ id: string }>();
  const toast = useToast();
  const qc = useQueryClient();
  const campaignId = Number(id);
  const userTz = useUserTimezone();

  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [showDeleted, setShowDeleted] = useState(false);
  // Seeded from ?filter= so other pages can deep-link into a status — the
  // Replies stat on the campaign stats page links here with filter=replied.
  const [searchParams] = useSearchParams();
  const [contactFilter, setContactFilter] = useState(() => searchParams.get('filter') ?? '');
  const [leadFilter, setLeadFilter] = useState(() => leadFilterFromUrl(searchParams.get('lead')));
  const perPage = 100;

  // Contact stats modal
  const [statsModalContact, setStatsModalContact] = useState<CampaignContact | null>(null);
  const [statsData, setStatsData] = useState<ContactStatsResponse | null>(null);
  const [statsLoading, setStatsLoading] = useState(false);

  const { data: campaign } = useQuery({
    queryKey: ['campaign-editor', id],
    queryFn: () => campaignsSvc.getCampaign(campaignId),
    enabled: !!id,
  });

  const isEditable = campaign?.status === 'draft';

  const { data: contactsData, isLoading } = useQuery({
    queryKey: ['campaign-contacts', id, search, page, showDeleted, contactFilter, leadFilter],
    queryFn: () => campaignsSvc.getCampaignContacts(campaignId, { search: search || undefined, page, perPage, showDeleted: showDeleted || undefined, contactFilter: contactFilter || undefined, lead: leadParam(leadFilter) }),
    enabled: !!id,
  });
  const contacts = contactsData?.data ?? [];
  const paginationTotal = contactsData?.meta?.total ?? 0;
  const statusCounts = contactsData?.statusCounts;

  // Separate count query for the header (matches PHP's dedicated COUNT query)
  const { data: recipientCount = 0 } = useQuery({
    queryKey: ['campaign-contact-count', id],
    queryFn: () => campaignsSvc.getCampaignContactCount(campaignId),
    enabled: !!id,
  });

  // ── Add contact modal ──
  const [showAddModal, setShowAddModal] = useState(false);
  const [patientSearch, setPatientSearch] = useState('');

  const { data: patientResults = [], isFetching: searchingPatients } = useQuery({
    queryKey: ['patient-search', patientSearch],
    queryFn: async () => {
      const res = await apiClient.get('/patients/search', { params: { q: patientSearch } });
      const rows = (res.data?.data ?? []) as Array<Record<string, any>>;
      return rows.map(r => ({
        patientID: r.patientId ?? r.patientID,
        firstName: r.firstName ?? '',
        lastName: r.lastName ?? '',
        email: r.email ?? '',
      })) as PatientResult[];
    },
    enabled: patientSearch.length >= 2,
  });

  const invalidateContacts = useCallback(() => {
    qc.invalidateQueries({ queryKey: ['campaign-contacts', id] });
    qc.invalidateQueries({ queryKey: ['campaign-contact-count', id] });
  }, [qc, id]);

  const addMut = useMutation({
    mutationFn: (data: campaignsSvc.AddContactData) => campaignsSvc.addCampaignContact(campaignId, data),
    onSuccess: () => { toast.success('Contact added'); invalidateContacts(); },
    onError: () => toast.error('Failed to add contact'),
  });

  const removeMut = useMutation({
    mutationFn: (contactId: number) => campaignsSvc.removeCampaignContact(campaignId, contactId),
    onSuccess: () => { toast.success('Contact removed'); invalidateContacts(); },
    onError: () => toast.error('Failed to remove contact'),
  });

  const clearMut = useMutation({
    mutationFn: () => campaignsSvc.clearCampaignContacts(campaignId),
    onSuccess: () => { toast.success('Queued contacts cleared'); invalidateContacts(); },
    onError: () => toast.error('Failed to clear contacts'),
  });

  const restoreMut = useMutation({
    mutationFn: (contactId: number) => campaignsSvc.restoreCampaignContact(campaignId, contactId),
    onSuccess: () => { toast.success('Contact restored'); invalidateContacts(); },
    onError: () => toast.error('Failed to restore contact'),
  });

  const handleAddPatient = useCallback((p: PatientResult) => {
    addMut.mutate({ patient_id: p.patientID, email: p.email, first_name: p.firstName, last_name: p.lastName });
    setShowAddModal(false);
    setPatientSearch('');
  }, [addMut]);

  // ── Open contact stats modal ──
  // 'failed'/'invalid' rows are openable: the send never reached SendGrid so there
  // are no email_events, but the modal is the only place the reason is surfaced.
  const openContactStats = useCallback(async (row: CampaignContact) => {
    const status = row.deliveryStatus || 'pending';
    if (status === 'pending' || status === 'queued') return;
    setStatsModalContact(row);
    setStatsData(null);
    setStatsLoading(true);
    try {
      const data = await campaignsSvc.getContactStats(campaignId, row.emailCampaignContactID);
      setStatsData(data);
    } catch {
      toast.error('Failed to load contact stats');
      setStatsModalContact(null);
    } finally {
      setStatsLoading(false);
    }
  }, [campaignId, toast]);

  const closeContactStats = useCallback(() => {
    setStatsModalContact(null);
    setStatsData(null);
  }, []);

  // The lead modal opens over the list; its Spam / Trash / Convert can change
  // what the Lead column and the open contact show, so both reload after it.
  const openContactId = statsModalContact?.emailCampaignContactID ?? null;
  const leadModal = useContactLeadModal(useCallback(() => {
    void qc.invalidateQueries({ queryKey: ['campaign-contacts', id] });
    if (openContactId != null) {
      campaignsSvc.getContactStats(campaignId, openContactId).then(setStatsData).catch(() => undefined);
    }
  }, [qc, id, campaignId, openContactId]));

  // ── Table columns ──
  const columns: DataTableColumn<CampaignContact>[] = useMemo(() => [
    {
      key: 'email',
      header: 'Email',
    },
    {
      key: 'firstName',
      header: 'First Name',
      render: (row) => <span>{row.pFirstName || row.firstName || ''}</span>,
    },
    {
      key: 'lastName',
      header: 'Last Name',
      render: (row) => <span>{row.pLastName || row.lastName || ''}</span>,
    },
    {
      key: 'deliveryStatus',
      header: 'Status',
      render: (row) => {
        const s = row.deliveryStatus || 'pending';
        const cls: Record<string, string> = {
          delivered: 'ccs-status-delivered',
          pending: 'ccs-status-pending',
          deferred: 'ccs-status-pending',
          processed: 'ccs-status-processed',
          queued: 'ccs-status-queued',
          failed: 'ccs-status-failed',
          invalid: 'ccs-status-invalid',
          bounce: 'ccs-status-bounce',
          dropped: 'ccs-status-dropped',
        };
        return (
          <span
            className={`ccs-status-badge ${cls[s] || 'ccs-status-default'}${row.reason ? ' has-reason' : ''}`}
            title={row.reason || undefined}
          >
            {s.charAt(0).toUpperCase() + s.slice(1)}
          </span>
        );
      },
    },
    {
      key: 'stats',
      header: 'Stats',
      render: (row) => {
        const opens = row.open ?? 0;
        const clicks = row.clicks ?? 0;
        const spam = row.spamReport ?? 0;
        const unsub = row.unsubscribe ?? 0;
        return (
          <div className="ccs-stats-cell">
            <span className={`ccs-stat-mini${opens > 0 ? ' has-value' : ''}`} title="Opens">
              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21.2 8.4c.5.38.8.97.8 1.6v10a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V10a2 2 0 0 1 .8-1.6l8-6a2 2 0 0 1 2.4 0l8 6Z"/><path d="m22 10-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 10"/></svg>
              {' '}{opens}
            </span>
            <span className={`ccs-stat-mini${clicks > 0 ? ' has-value' : ''}`} title="Clicks">
              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M15 15l-2 5L9 9l11 4-5 2z"/><path d="M22 22l-5-5"/></svg>
              {' '}{clicks}
            </span>
            {spam > 0 && (
              <span className="ccs-stat-mini has-danger" title="Spam Reports">
                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z"/><path d="M12 9v4"/><path d="M12 17h.01"/></svg>
                {' '}{spam}
              </span>
            )}
            {unsub > 0 && (
              <span className="ccs-stat-mini has-warning" title="Unsubscribes">
                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><line x1="22" x2="16" y1="11" y2="11"/></svg>
                {' '}{unsub}
              </span>
            )}
            {/* Replies have no count — this contact either answered or didn't,
                and the reply itself is one click away in the stats modal. */}
            {row.replied ? (
              <span className="ccs-stat-mini has-reply" title="Replied to this campaign">
                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="9 17 4 12 9 7"/><path d="M20 18v-2a4 4 0 0 0-4-4H4"/></svg>
                {' '}Replied
              </span>
            ) : null}
          </div>
        );
      },
    },
    {
      key: 'leads',
      header: 'Lead',
      render: (row) => <ContactLeadCell leads={row.leads} />,
    },
    {
      key: 'createDate',
      header: 'Date Added',
      render: (row) => row.createDate ? fmtDate(row.createDate, userTz) : '—',
    },
    ...(isEditable ? [{
      key: 'actions' as const,
      header: '',
      render: (row: CampaignContact) => {
        if (row.recordStatus === 0) {
          return (
            <button
              type="button"
              title="Restore"
              onClick={(e) => { e.stopPropagation(); restoreMut.mutate(row.emailCampaignContactID); }}
              className="inline-flex items-center justify-center h-7 w-7 rounded text-green-500 hover:text-green-700 hover:bg-green-50 transition-colors"
            >
              <RotateCcw className="h-4 w-4" />
            </button>
          );
        }
        return (
          <button
            type="button"
            title="Remove"
            onClick={(e) => { e.stopPropagation(); removeMut.mutate(row.emailCampaignContactID); }}
            className="inline-flex items-center justify-center h-7 w-7 rounded text-red-400 hover:text-red-600 hover:bg-red-50 transition-colors"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        );
      },
    }] : []),
  ], [isEditable, removeMut, restoreMut, userTz]);

  // ── Determine back link ──
  const isSent = campaign?.status === 'sent' || campaign?.status === 'sending';
  const backLink = isSent
    ? `/marketing/campaigns/${id}/stats`
    : campaign?.status === 'scheduled' ? `/marketing/campaigns/${id}/scheduled` : `/marketing/campaigns/${id}`;

  // Status filter options — dot colour / text colour mirror the row status badges above.
  const FILTER_OPTIONS = useMemo<FilterSelectOption[]>(() => {
    const sc = statusCounts;
    return [
      { label: 'All Contacts', value: '', count: recipientCount, color: '#3b82f6', textColor: '#1d4ed8' },
      { label: 'Delivered', value: 'delivered', count: sc?.delivered, color: '#22c55e', textColor: '#15803d' },
      { label: 'Pending', value: 'pending', count: sc?.pending, color: '#f59e0b', textColor: '#b45309' },
      { label: 'Queued', value: 'queued', count: sc?.queued, color: '#94a3b8', textColor: '#475569' },
      { label: 'Failed', value: 'failed', count: sc?.failed, color: '#ef4444', textColor: '#b91c1c' },
      { label: 'Invalid Address', value: 'invalid', count: sc?.invalid, color: '#9f1239', textColor: '#881337' },
      { label: 'Bounced', value: 'bounce', count: sc?.bounce, color: '#dc2626', textColor: '#991b1b' },
      { label: 'Dropped', value: 'dropped', count: sc?.dropped, color: '#a855f7', textColor: '#7e22ce' },
      { label: 'Opened', value: 'opened', count: sc?.opened, color: '#00aff0', textColor: '#0369a1' },
      { label: 'Not Opened', value: 'not_opened', count: sc?.not_opened, color: '#cbd5e1', textColor: '#64748b' },
      { label: 'Clicked', value: 'clicked', count: sc?.clicked, color: '#8b5cf6', textColor: '#6d28d9' },
      { label: 'Replied', value: 'replied', count: sc?.replied, color: '#17a2b8', textColor: '#0e7490' },
      { label: 'Unsubscribed', value: 'unsubscribed', count: sc?.unsubscribed, color: '#f97316', textColor: '#c2410c' },
      { label: 'Spam reports', value: 'spam', count: sc?.spam, color: '#be123c', textColor: '#9f1239' },
    ];
  }, [statusCounts, recipientCount]);

  return (
    <ContactLeadOpener value={leadModal.openLead}>
      <style>{`
        .ccs-status-badge {
          display: inline-block; padding: 3px 10px; border-radius: 12px;
          font-size: 12px; font-weight: 600; text-transform: capitalize;
        }
        .ccs-status-delivered { background: #d4edda; color: #155724; }
        .ccs-status-pending { background: #fff3cd; color: #856404; }
        .ccs-status-queued { background: #e2e8f0; color: #475569; }
        .ccs-status-failed { background: #f5c6cb; color: #842029; }
        .ccs-status-invalid { background: #ffe4e6; color: #881337; }
        .ccs-status-processed { background: #cce5ff; color: #004085; }
        .ccs-status-bounce { background: #f8d7da; color: #721c24; }
        .ccs-status-dropped { background: #f3e8ff; color: #6b21a8; }
        .ccs-status-default { background: #e9ecef; color: #666; }
        .ccs-status-badge.has-reason { cursor: help; }

        .ccs-stats-cell { display: flex; gap: 6px; align-items: center; flex-wrap: wrap; }
        .ccs-stat-mini {
          display: inline-flex; align-items: center; gap: 3px;
          padding: 2px 6px; border-radius: 4px; font-size: 11px; font-weight: 600;
          background: #f0f0f0; color: #999;
        }
        .ccs-stat-mini.has-value { background: #e7f3ff; color: #00aff0; }
        .ccs-stat-mini.has-danger { background: #ffeaea; color: #dc3545; }
        .ccs-stat-mini.has-warning { background: #fff8e1; color: #e65100; }
        .ccs-stat-mini.has-reply { background: #e0f7fa; color: #0e7490; font-weight: 600; }

        .ccs-clickable-row { cursor: pointer; }
        .ccs-clickable-row:hover { background: #f9fafb; }
        .ccs-deleted-row { opacity: 0.5; background: #fafafa; }

        /* ── Contact Stats Modal ── */
        .cs-overlay {
          display: none; position: fixed; inset: 0; background: rgba(0,0,0,0.5);
          z-index: 9999; justify-content: center; align-items: flex-start;
          padding-top: 60px; overflow-y: auto;
        }
        .cs-overlay.active { display: flex; }
        .cs-modal {
          background: #fff; border-radius: 10px; width: 90%; max-width: 680px;
          box-shadow: 0 8px 30px rgba(0,0,0,0.25); margin-bottom: 40px;
        }
        .cs-header {
          display: flex; justify-content: space-between; align-items: center;
          padding: 16px 20px; border-bottom: 1px solid #e5e5e5;
        }
        .cs-header h3 { margin: 0; font-size: 16px; font-weight: 600; }
        .cs-close {
          background: none; border: none; font-size: 22px; cursor: pointer;
          color: #999; line-height: 1; padding: 0 4px;
        }
        .cs-close:hover { color: #333; }
        .cs-body { padding: 20px; }

        .cs-contact-header {
          display: flex; justify-content: space-between; align-items: flex-start;
          margin-bottom: 20px;
        }
        .cs-contact-info { flex: 1; }
        .cs-contact-name { font-size: 18px; font-weight: 600; color: #333; margin: 0 0 4px; }
        .cs-contact-email { color: #666; font-size: 14px; margin: 0; }
        .cs-patient-link {
          display: inline-flex; align-items: center; gap: 5px;
          color: #00aff0; text-decoration: none; font-size: 13px;
          padding: 6px 12px; border: 1px solid #ddd; border-radius: 5px;
          background: white; white-space: nowrap; flex-shrink: 0;
          transition: background 0.2s;
        }
        .cs-patient-link:hover { background: #f5f5f5; text-decoration: none; }

        .cs-section { margin-bottom: 20px; }
        .cs-section-title {
          font-size: 13px; font-weight: 600; color: #333;
          text-transform: uppercase; letter-spacing: 0.5px;
          margin-bottom: 12px; padding-bottom: 6px; border-bottom: 1px solid #e5e5e5;
        }

        .cs-timeline { display: flex; flex-direction: column; gap: 8px; }
        .cs-timeline-row { display: flex; gap: 8px; }
        .cs-timeline-row > .cs-timeline-item { flex: 1; }
        .cs-timeline-item {
          display: flex; align-items: center; gap: 10px;
          padding: 10px 14px; background: #f8f9fa; border-radius: 6px;
        }
        .cs-timeline-item.bounce { background: #fff5f5; }
        .cs-bounce-reason {
          font-size: 12px; color: #721c24; background: #fff5f5;
          padding: 8px 14px; border-radius: 6px; line-height: 1.5;
        }
        .cs-timeline-icon {
          width: 28px; height: 28px; border-radius: 50%;
          display: flex; align-items: center; justify-content: center;
          font-size: 12px; flex-shrink: 0;
        }
        .cs-timeline-icon.sent { background: #e7f3ff; color: #00aff0; }
        .cs-timeline-icon.delivered { background: #d4edda; color: #155724; }
        .cs-timeline-icon.bounce-icon { background: #f8d7da; color: #721c24; }
        .cs-timeline-label { font-weight: 600; font-size: 13px; color: #333; }
        .cs-timeline-date { font-size: 12px; color: #888; margin-left: auto; }
        .cs-timeline-date.na { color: #ccc; }

        .cs-engagement { display: flex; gap: 12px; }
        .cs-stat-box {
          flex: 1; text-align: center; padding: 16px;
          background: #f8f9fa; border-radius: 8px;
        }
        .cs-stat-number { font-size: 28px; font-weight: 700; margin-bottom: 2px; }
        .cs-stat-number.opens { color: #28a745; }
        .cs-stat-number.clicks { color: #ffc107; }
        .cs-stat-number.zero { color: #ccc; }
        .cs-stat-label { font-size: 11px; color: #888; font-weight: 600; text-transform: uppercase; }
        .cs-stat-dates { font-size: 11px; color: #999; margin-top: 6px; line-height: 1.5; }

        .cs-url-table { width: 100%; border-collapse: collapse; font-size: 13px; }
        .cs-url-table th {
          text-align: left; padding: 8px 12px; font-size: 11px;
          font-weight: 600; text-transform: uppercase; color: #666;
          background: #f8f9fa; border-bottom: 1px solid #e5e5e5;
        }
        .cs-url-table th:last-child { text-align: right; }
        .cs-url-table td {
          padding: 10px 12px; border-bottom: 1px solid #f0f0f0; color: #333;
        }
        .cs-url-table td:last-child { text-align: right; font-weight: 600; }
        .cs-url-link {
          color: #00aff0; text-decoration: none; word-break: break-all;
          display: block;
        }
        .cs-url-link:hover { text-decoration: underline; }
        .cs-url-sub { font-size: 11px; color: #999; margin-top: 2px; }
        .cs-empty { text-align: center; color: #999; padding: 20px; font-size: 13px; }

        /* ── Replies ── */
        .cs-reply-list { display: flex; flex-direction: column; gap: 6px; }
        .cs-reply-row {
          display: flex; align-items: center; gap: 10px; width: 100%;
          padding: 10px 12px; border: 1px solid #e5e5e5; border-radius: 6px;
          background: #f7fdfe; color: #0e7490; text-align: left; cursor: pointer;
          font-size: 13px; transition: background 0.12s, border-color 0.12s;
        }
        .cs-reply-row:hover { background: #e0f7fa; border-color: #17a2b8; }
        .cs-reply-meta { display: flex; flex-direction: column; gap: 2px; min-width: 0; flex: 1; }
        .cs-reply-subject { font-weight: 600; color: #0e7490; }
        .cs-reply-preview {
          color: #666; font-size: 12px;
          overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
        }
        .cs-reply-date { color: #888; font-size: 12px; white-space: nowrap; }

        /* Stacked ABOVE the stats modal (which sits at 9999), not replacing it. */
        .cs-reply-overlay {
          display: none; position: fixed; inset: 0; background: rgba(0,0,0,0.5);
          z-index: 10000; justify-content: center; align-items: flex-start;
          padding: 40px 20px; overflow-y: auto;
        }
        .cs-reply-overlay.active { display: flex; }
        .cs-reply-modal {
          background: white; border-radius: 8px; width: 100%; max-width: 760px;
          box-shadow: 0 10px 40px rgba(0,0,0,0.25); overflow: hidden;
        }
        .cs-reply-modal-sub {
          padding: 10px 20px; background: #fafafa; border-bottom: 1px solid #eee;
          font-size: 12px; color: #666;
        }
        .cs-reply-modal-body { padding: 0; }
        .cs-reply-frame {
          width: 100%; height: 60vh; border: none; display: block; background: white;
        }
        .cs-reply-plain {
          padding: 20px; font-size: 13px; color: #333; white-space: pre-wrap;
          word-break: break-word;
        }
        .cs-loading { text-align: center; padding: 40px; color: #999; }
      `}</style>

      <div className="flex flex-col h-full overflow-hidden p-6">
        {/* Header */}
        <div className="flex-shrink-0 space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Link to={backLink} className="p-1 rounded hover:bg-slate-100">
                <ArrowLeft className="h-5 w-5 text-slate-500" />
              </Link>
              <h1 className="text-xl font-semibold text-slate-800">Campaign Contacts</h1>
              <div className="flex items-center gap-2 ml-2">
                <span className="text-2xl font-bold text-blue-500">{recipientCount.toLocaleString()}</span>
                <span className="text-sm font-semibold text-slate-500">{recipientCount === 1 ? 'recipient' : 'recipients'}</span>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {isSent && (
                <Link
                  to={`/marketing/campaigns/${id}/stats`}
                  className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Campaign Stats
                </Link>
              )}
              {isEditable && (
                <>
                  <button type="button" onClick={() => { if (window.confirm('Clear all queued/pending contacts?')) clearMut.mutate(); }}
                    disabled={clearMut.isPending}
                    className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50">
                    <Trash2 className="h-4 w-4" />
                    Clear All Queued
                  </button>
                  <button type="button" onClick={() => setShowAddModal(true)}
                    className="inline-flex items-center gap-1.5 rounded-md bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700 transition-colors">
                    <Plus className="h-4 w-4" />
                    Add Patient
                  </button>
                </>
              )}
            </div>
          </div>

          {/* Status filter + search */}
          <div className="flex items-center gap-4">
            {!isEditable && (
              <FilterSelect
                options={FILTER_OPTIONS}
                value={contactFilter}
                onChange={(v) => { setContactFilter(v); setPage(1); }}
                label="Filter by status"
                className="w-60"
              />
            )}
            {!isEditable && (
              <ContactLeadFilter
                value={leadFilter}
                onChange={(v) => { setLeadFilter(v); setPage(1); }}
                total={recipientCount}
                leadCount={statusCounts?.lead}
                recipientCount={statusCounts?.leadRecipient}
                referralCount={statusCounts?.leadReferral}
              />
            )}
            {isEditable && (
              <label className="inline-flex items-center gap-2 text-sm text-gray-600 cursor-pointer select-none whitespace-nowrap">
                <input type="checkbox" checked={showDeleted}
                  onChange={e => { setShowDeleted(e.target.checked); setPage(1); }}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
                Show Deleted
              </label>
            )}
            <div className="ml-auto">
              <Input
                value={search}
                onChange={e => { setSearch(e.target.value); setPage(1); }}
                placeholder="Search contacts..."
                className="w-64"
                leftAddon={<Search className="h-4 w-4" />}
              />
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="flex-1 min-h-0 mt-4">
          <DataTable<CampaignContact>
            data={contacts}
            columns={columns}
            keyField="emailCampaignContactID"
            loading={isLoading}
            page={page}
            pageSize={perPage}
            total={paginationTotal}
            onPageChange={setPage}
            onRowClick={(row) => openContactStats(row)}
            rowClassName={(row) => {
              if (row.recordStatus === 0) return 'ccs-deleted-row';
              const s = row.deliveryStatus || 'pending';
              return (s !== 'pending' && s !== 'queued') ? 'ccs-clickable-row' : '';
            }}
          />
        </div>

        {/* Add Patient Modal */}
        {showAddModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
            <div className="w-full max-w-lg rounded-lg bg-white shadow-xl">
              <div className="flex items-center justify-between border-b border-gray-200 px-5 py-4">
                <h3 className="text-lg font-semibold text-gray-800">Add Patient</h3>
                <button type="button" onClick={() => setShowAddModal(false)} className="text-gray-400 hover:text-gray-600">
                  <X className="h-5 w-5" />
                </button>
              </div>
              <div className="px-5 py-4">
                <div className="mb-3">
                  <Input
                    value={patientSearch}
                    onChange={e => setPatientSearch(e.target.value)}
                    placeholder="Search by name or email (min 2 chars)..."
                    autoFocus
                    leftAddon={<Search className="h-4 w-4" />}
                  />
                </div>
                <div className="max-h-60 overflow-y-auto">
                  {searchingPatients ? (
                    <div className="flex justify-center py-4"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>
                  ) : patientResults.length === 0 ? (
                    <p className="text-center py-4 text-sm text-gray-400">
                      {patientSearch.length < 2 ? 'Type at least 2 characters' : 'No patients found'}
                    </p>
                  ) : (
                    patientResults.map(p => (
                      <button key={p.patientID} type="button" onClick={() => handleAddPatient(p)}
                        className="flex w-full items-center justify-between px-3 py-2 text-sm hover:bg-blue-50 rounded transition-colors">
                        <div>
                          <span className="font-medium text-gray-700">{p.firstName} {p.lastName}</span>
                          <span className="ml-2 text-gray-400">{p.email}</span>
                        </div>
                        <Plus className="h-4 w-4 text-blue-500" />
                      </button>
                    ))
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ── Contact Stats Modal ── */}
      <div
        className={`cs-overlay${statsModalContact && !leadModal.isOpen ? ' active' : ''}`}
        onClick={(e) => { if (e.target === e.currentTarget) closeContactStats(); }}
      >
        <div className="cs-modal">
          <div className="cs-header">
            <h3>Contact Stats</h3>
            <button className="cs-close" onClick={closeContactStats}>&times;</button>
          </div>
          <div className="cs-body">
            {statsLoading && <div className="cs-loading"><Loader2 className="h-6 w-6 animate-spin inline-block" /> Loading...</div>}
            {!statsLoading && statsData && <ContactStatsBody data={statsData} />}
          </div>
        </div>
      </div>

      {leadModal.modal}
    </ContactLeadOpener>
  );
}

// ─── Contact Stats Modal Body ────────────────────────────────────────────────

function ContactStatsBody({ data }: { data: ContactStatsResponse }) {
  const userTz = useUserTimezone();
  const c = data.contact;
  const name = `${c.firstName || ''} ${c.lastName || ''}`.trim();
  const delivery = data.delivery;
  const replies = data.replies ?? [];
  const [openReply, setOpenReply] = useState<CampaignContactReply | null>(null);
  const totalOpens = data.opens.total_opens ?? 0;
  const totalClicks = data.clicks.total_clicks ?? 0;

  return (
    <>
      {/* Contact header */}
      <div className="cs-contact-header">
        <div className="cs-contact-info">
          {name && <p className="cs-contact-name">{name}</p>}
          <p className="cs-contact-email">{c.email}</p>
        </div>
        {c.patientID && (
          <Link
            to={`/patients/${c.patientID}`}
            className="cs-patient-link"
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2" />
              <circle cx="12" cy="7" r="4" />
            </svg>
            View Patient
          </Link>
        )}
      </div>

      {/* Delivery Timeline */}
      <div className="cs-section">
        <div className="cs-section-title">Delivery Timeline</div>
        <div className="cs-timeline">
          {/* Sent + Delivered side by side */}
          <div className="cs-timeline-row">
            <div className="cs-timeline-item">
              <div className="cs-timeline-icon sent">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="22" x2="11" y1="2" y2="13" /><polygon points="22 2 15 22 11 13 2 9 22 2" />
                </svg>
              </div>
              <span className="cs-timeline-label">Sent</span>
              <span className={`cs-timeline-date${c.sentDate ? '' : ' na'}`}>
                {c.sentDate ? fmtDate(c.sentDate, userTz) : '—'}
              </span>
            </div>

            <div className="cs-timeline-item">
              <div className="cs-timeline-icon delivered">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              </div>
              <span className="cs-timeline-label">Delivered</span>
              <span className={`cs-timeline-date${delivery.delivered ? '' : ' na'}`}>
                {delivery.delivered ? fmtDate(delivery.delivered.first_at, userTz) : '—'}
              </span>
            </div>
          </div>

          {/* Send failure (only if applicable) — never reached the mail provider,
              so there is no delivery event; the reason column is all we have. */}
          {(c.status === 'failed' || c.status === 'invalid') && (
            <>
              <div className="cs-timeline-item bounce">
                <div className="cs-timeline-icon bounce-icon">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z" />
                    <path d="M12 9v4" /><path d="M12 17h.01" />
                  </svg>
                </div>
                <span className="cs-timeline-label">
                  {c.status === 'invalid' ? 'Not Sent — Invalid Address' : 'Send Failed'}
                </span>
                <span className={`cs-timeline-date${c.updateDate ? '' : ' na'}`}>
                  {c.updateDate ? fmtDate(c.updateDate, userTz) : '—'}
                </span>
              </div>
              <div className="cs-bounce-reason">
                {c.reason || 'No failure reason was recorded for this contact.'}
              </div>
            </>
          )}

          {/* Bounced (only if applicable) */}
          {(delivery.bounce || c.status === 'bounce') && (
            <>
              <div className="cs-timeline-item bounce">
                <div className="cs-timeline-icon bounce-icon">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10" /><line x1="15" x2="9" y1="9" y2="15" /><line x1="9" x2="15" y1="9" y2="15" />
                  </svg>
                </div>
                <span className="cs-timeline-label">Bounced</span>
                <span className={`cs-timeline-date${delivery.bounce?.first_at ? '' : ' na'}`}>
                  {delivery.bounce?.first_at ? fmtDate(delivery.bounce.first_at, userTz) : '—'}
                </span>
              </div>
              {c.reason && (
                <div className="cs-bounce-reason">{c.reason}</div>
              )}
            </>
          )}

          {/* Dropped (only if applicable) */}
          {(delivery.dropped || c.status === 'dropped') && (
            <>
              <div className="cs-timeline-item bounce">
                <div className="cs-timeline-icon bounce-icon">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10" /><line x1="4.93" x2="19.07" y1="4.93" y2="19.07" />
                  </svg>
                </div>
                <span className="cs-timeline-label">Dropped</span>
                <span className={`cs-timeline-date${delivery.dropped?.first_at ? '' : ' na'}`}>
                  {delivery.dropped?.first_at ? fmtDate(delivery.dropped.first_at, userTz) : '—'}
                </span>
              </div>
              {c.reason && (
                <div className="cs-bounce-reason">{c.reason}</div>
              )}
            </>
          )}
        </div>
        {/* A campaign is one message, so its leads sit under its delivery. */}
        <ContactLeadList leads={data.leads ?? []} tz={userTz} className="mt-2" />
      </div>

      {/* Engagement */}
      <div className="cs-section">
        <div className="cs-section-title">Engagement</div>
        <div className="cs-engagement">
          <div className="cs-stat-box">
            <div className={`cs-stat-number ${totalOpens > 0 ? 'opens' : 'zero'}`}>
              {totalOpens}
            </div>
            <div className="cs-stat-label">Total Opens</div>
            {totalOpens > 0 && (
              <div className="cs-stat-dates">
                First: {fmtDate(data.opens.first_open, userTz)}<br />
                Last: {fmtDate(data.opens.last_open, userTz)}
              </div>
            )}
          </div>
          <div className="cs-stat-box">
            <div className={`cs-stat-number ${totalClicks > 0 ? 'clicks' : 'zero'}`}>
              {totalClicks}
            </div>
            <div className="cs-stat-label">Total Clicks</div>
            {totalClicks > 0 && (
              <div className="cs-stat-dates">
                First: {fmtDate(data.clicks.first_click, userTz)}<br />
                Last: {fmtDate(data.clicks.last_click, userTz)}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Clicked URLs */}
      <div className="cs-section">
        <div className="cs-section-title">Clicked URLs</div>
        {data.urlClicks.length > 0 ? (
          <table className="cs-url-table">
            <thead>
              <tr>
                <th>Link</th>
                <th>Clicks</th>
              </tr>
            </thead>
            <tbody>
              {data.urlClicks.map((u, i) => (
                <tr key={i}>
                  <td>
                    <a href={u.url} target="_blank" rel="noopener noreferrer" className="cs-url-link">
                      {u.url}
                    </a>
                    <div className="cs-url-sub">
                      First: {fmtDate(u.first_click, userTz)} &middot; Last: {fmtDate(u.last_click, userTz)}
                    </div>
                  </td>
                  <td>{u.total_clicks}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="cs-empty">No links clicked</div>
        )}
      </div>

      {/* Replies — only rendered when there are any, so the modal does not grow
          an empty section for the overwhelming majority of contacts. */}
      {replies.length > 0 && (
        <div className="cs-section">
          <div className="cs-section-title">
            {replies.length === 1 ? 'Reply' : `Replies (${replies.length})`}
          </div>
          <div className="cs-reply-list">
            {replies.map((reply) => (
              <button
                key={reply.id}
                type="button"
                className="cs-reply-row"
                onClick={() => setOpenReply(reply)}
                title="Open this reply"
              >
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="9 17 4 12 9 7" /><path d="M20 18v-2a4 4 0 0 0-4-4H4" />
                </svg>
                <span className="cs-reply-meta">
                  <span className="cs-reply-subject">{reply.subject || '(No subject)'}</span>
                  <span className="cs-reply-preview">{reply.preview || '—'}</span>
                </span>
                <span className="cs-reply-date">
                  {fmtDate(reply.receivedDate, userTz)}
                  {reply.hadAttachments && <span title="Had attachments"> 📎</span>}
                </span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Nested reply modal. Stacked above the stats modal rather than replacing
          it, so closing this returns to the stats the user was already reading. */}
      {openReply && (
        <div
          className="cs-reply-overlay active"
          onClick={(e) => { if (e.target === e.currentTarget) setOpenReply(null); }}
        >
          <div className="cs-reply-modal">
            <div className="cs-header">
              <h3>{openReply.subject || 'Reply'}</h3>
              <button className="cs-close" onClick={() => setOpenReply(null)}>&times;</button>
            </div>
            <div className="cs-reply-modal-sub">
              From {c.email} &middot; {fmtDate(openReply.receivedDate, userTz)}
              {openReply.patientId > 0 && (
                <>
                  {' '}&middot;{' '}
                  <Link to={`/patients/${openReply.patientId}`} className="cs-url-link">
                    Open patient
                  </Link>
                </>
              )}
            </div>
            <div className="cs-reply-modal-body">
              {openReply.bodyIsFull ? (
                // The stored body is the real message HTML, quoted chain and
                // all. Sandboxed: it is content a patient sent us.
                <iframe
                  title="Reply"
                  className="cs-reply-frame"
                  sandbox=""
                  srcDoc={openReply.body}
                />
              ) : (
                // No comm_log row — an unmatched sender — so only the stored
                // plain-text excerpt exists.
                <div className="cs-reply-plain">{openReply.body || '(No content)'}</div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
